import { Link, Typography, styled, Stack, Box } from '@mui/material';
import { browserName, browserVersion } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
const UPDATE_CHROME_LINK = 'https://support.google.com/chrome/answer/95414';
const UPDATE_EDGE_LINK =
    'https://support.microsoft.com/topic/microsoft-edge-update-settings-af8aaca2-1b69-4870-94fe-18822dbb7ef1';

const DOWNLOAD_CHROME_LINK = 'https://www.google.com/chrome/';
const DOWNLOAD_EDGE_LINK = 'https://www.microsoft.com/edge/download';

const StyledContainer = styled(Box)`
    max-width: 100%;
    align-items: center;
    text-align: center;

    & .MuiTypography-root {
        text-align: center;
    }
`;

const UpdateGuide = styled(Typography)`
    margin-top: 24px;
    display: flex;
    flex-flow: column;
`;

const ContentTitle = styled(Typography)`
    font-size: 24px;
    font-weight: 400;
    line-height: 32px;
    margin-bottom: 8px;
` as typeof Typography;

const Body = styled(Stack)`
    flex: 1 1 auto;
    align-items: center;
    display: block;
    overflow-y: auto;
`;

export enum BrowserCheckResults {
    Unsupported = 'unsupported',
    OutOfDate = 'out-of-date',
    Supported = 'supported'
}

export type UnsupportedBrowserScreenProps = {
    reason: BrowserCheckResults;
};

export const UnsupportedBrowserScreen = (props: UnsupportedBrowserScreenProps) => {
    const { reason } = props;

    const { t } = useTranslation('index_client');

    const browserNameLower = browserName.toLowerCase();

    if (reason === BrowserCheckResults.Unsupported) {
        return (
            <StyledContainer>
                <ContentTitle component="h1">{t('browser-unsupported-title')}</ContentTitle>
                <Body>
                    {t('browser-unsupported-description-start')}
                    &nbsp;
                    <Link href={DOWNLOAD_EDGE_LINK} target="_blank">
                        {t('browser-unsupported-edge')}
                    </Link>
                    &nbsp;
                    {t('browser-unsupported-or')}
                    &nbsp;
                    <Link href={DOWNLOAD_CHROME_LINK} target="_blank">
                        {t('browser-unsupported-chrome')}
                    </Link>
                    &nbsp;
                    {t('browser-unsupported-description-end')}
                    {window.location.origin}
                </Body>
            </StyledContainer>
        );
    }

    let updateLink: string | null = null;
    if (browserNameLower === 'chrome') {
        updateLink = UPDATE_CHROME_LINK;
    } else if (browserNameLower === 'edge') {
        updateLink = UPDATE_EDGE_LINK;
    }

    return (
        <StyledContainer>
            <ContentTitle component="h1">{t('browser-out-of-date-title')}</ContentTitle>
            {t('browser-out-of-date-description', { browserName, browserVersion })}
            {Boolean(updateLink) && (
                <UpdateGuide>
                    {t('browser-out-of-date-learn-more')}
                    <Link href={updateLink!} target="_blank">
                        {t('browser-out-of-date-how-to-update')}
                    </Link>
                </UpdateGuide>
            )}
        </StyledContainer>
    );
};
